/**
 * Tool call inline block — Claude-Code-style.
 *
 * Single-line monospace summary that reads like a function call
 * (`grep_skills("invoice")`); click to expand args + result.
 * No emojis, no chrome, no avatars — just the function signature
 * with a tiny dim state indicator.
 */

import React, { useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Spin, Tooltip } from 'antd';
import type { ToolCallItem } from '../types';
import { useInspector } from './InspectorContext';
import { extractEvalRef } from '../evalToolResult';
import './ToolCallBlock.css';

interface ToolCallBlockProps {
  call: ToolCallItem;
}

/** Match a canonical UUID (8-4-4-4-12 hex with dashes). Used to scrub
 *  internal ids out of the inline tool-call signature display — the user
 *  does not need (or want) to see them. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUuid(value: unknown): boolean {
  return typeof value === 'string' && UUID_RE.test(value.trim());
}

/** Placeholder shown in place of a UUID. Carries no identifying info —
 *  by design, since the inline view should never expose internal ids. */
const ID_MASK = '…';

function quote(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') {
    if (isUuid(value)) return ID_MASK;
    const truncated = value.length > 40 ? value.slice(0, 37) + '…' : value;
    return JSON.stringify(truncated);
  }
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    const s = JSON.stringify(value);
    return s.length > 40 ? s.slice(0, 37) + '…' : s;
  } catch {
    return String(value);
  }
}

/** Compact function-call rendering of the most distinguishing args.
 *
 *  Every entity-id arg (chatbot_id, skill_id, data_bundle_id, file_id,
 *  eval_conversation_id, etc.) flows through ``quote`` which masks UUIDs
 *  to ``…``. The user sees the verb + the human-facing distinguisher
 *  (field name, search pattern, message text) but not the internal id —
 *  in line with persona §2 rule 7 ("never expose internal IDs in
 *  user-facing output"). The expand panel keeps the raw args for debugging.
 */
function formatCall(tool: string, args: Record<string, unknown>): string {
  const a = args || {};
  switch (tool) {
    case 'list_agents':
    case 'list_skills':
    case 'list_data_bundles':
      return a.name_contains ? `${tool}(${quote(a.name_contains)})` : `${tool}()`;
    case 'read_agent':
      return `read_agent(${quote(a.chatbot_id)})`;
    case 'read_skill':
      return `read_skill(${quote(a.skill_id)})`;
    case 'read_data_bundle':
      return `read_data_bundle(${quote(a.data_bundle_id)})`;
    case 'read_tenant_context':
      return a.target_tenant_id ? `read_tenant_context(${quote(a.target_tenant_id)})` : `read_tenant_context()`;
    case 'list_tenants':
      return a.name_contains ? `list_tenants(${quote(a.name_contains)})` : `list_tenants()`;
    case 'grep_agents':
    case 'grep_skills':
    case 'grep_data_bundles':
      return a.fields && a.fields !== 'all'
        ? `${tool}(${quote(a.pattern)}, fields=${quote(a.fields)})`
        : `${tool}(${quote(a.pattern)})`;
    case 'glob_agents':
    case 'glob_skills':
    case 'glob_data_bundles':
      return `${tool}(${quote(a.name_pattern)})`;
    case 'send_test_message_to_agent':
      return a.target_tenant_id
        ? `send_test_message_to_agent(${quote(a.chatbot_id)}, ${quote(a.user_message)}, as ${quote(a.target_tenant_id)})`
        : `send_test_message_to_agent(${quote(a.chatbot_id)}, ${quote(a.user_message)})`;
    case 'web_search':
      // A search by its query; a GPT page open by what it opened. Persisted
      // args come back key-sorted, so the query is named explicitly here.
      if (a.query) return `web_search(${quote(a.query)})`;
      return a.action ? `web_search(${a.action}, ${quote(a.url ?? a.pattern)})` : 'web_search()';
    case 'open_agent_simulation':
      return `open_agent_simulation(${quote(a.chatbot_id)})`;
    case 'list_eval_conversations':
      return a.target_chatbot_id
        ? `list_eval_conversations(${quote(a.target_chatbot_id)})`
        : `list_eval_conversations()`;
    case 'read_eval_conversation':
      return `read_eval_conversation(${quote(a.eval_conversation_id)})`;
    case 'propose_edit_agent':
      return `propose_edit_agent(${quote(a.chatbot_id)}, ${quote(a.field)})`;
    case 'propose_edit_skill':
      return `propose_edit_skill(${quote(a.skill_id)}, ${quote(a.field)})`;
    case 'propose_edit_data_bundle':
      return `propose_edit_data_bundle(${quote(a.data_bundle_id)}, ${quote(a.field)})`;
    case 'propose_edit_tenant_context':
      // New paragraph + optional target tenant; the text itself isn't surfaced.
      return a.target_tenant_id ? `propose_edit_tenant_context(${quote(a.target_tenant_id)})` : `propose_edit_tenant_context()`;
    case 'propose_create_agent':
      return `propose_create_agent(${quote(a.chatbot_name)})`;
    case 'propose_create_skill':
      return `propose_create_skill(${quote(a.skill_name)})`;
    case 'propose_duplicate_agent': {
      // Surface the boolean knobs (whether the user is duplicating just
      // the agent or also its skills/bundles) so the inline summary is
      // self-explanatory. UUIDs are masked by ``quote``.
      const dupS = a.duplicate_skills ? '+skills' : '';
      const dupB = a.duplicate_data_bundles ? '+bundles' : '';
      const extras = [dupS, dupB].filter(Boolean).join(',');
      return `propose_duplicate_agent(${quote(a.chatbot_id)}${extras ? `, ${extras}` : ''})`;
    }
    case 'propose_attach_skill':
    case 'propose_detach_skill':
      return `${tool}(${quote(a.chatbot_id)}, ${quote(a.skill_id)})`;
    case 'propose_attach_bundle':
    case 'propose_detach_bundle':
      return `${tool}(${quote(a.chatbot_id)}, ${quote(a.data_bundle_id)})`;
    case 'propose_attach_bundle_to_skill':
    case 'propose_detach_bundle_from_skill':
      return `${tool}(${quote(a.skill_id)}, ${quote(a.data_bundle_id)})`;
    case 'propose_manage_files_on_agent':
      return `propose_manage_files_on_agent(${quote(a.chatbot_id)})`;
    case 'propose_manage_files_on_skill':
      return `propose_manage_files_on_skill(${quote(a.skill_id)})`;
    case 'propose_manage_files_on_tenant':
      return a.target_tenant_id ? `propose_manage_files_on_tenant(${quote(a.target_tenant_id)})` : `propose_manage_files_on_tenant()`;
    case 'propose_delete_agent':
      return `propose_delete_agent(${quote(a.chatbot_id)})`;
    case 'propose_delete_skill':
      return `propose_delete_skill(${quote(a.skill_id)})`;
    case 'propose_delete_data_bundle':
      return `propose_delete_data_bundle(${quote(a.data_bundle_id)})`;
    default:
      return `${tool}(${Object.keys(a).map((k) => `${k}=${quote(a[k])}`).join(', ')})`;
  }
}

/** Render a tool result for the expanded view. Tool returns are JSON
 *  strings from the backend; pretty-print them when parsable. */
function prettyResult(value: unknown): string {
  if (value == null) return '';
  if (typeof value !== 'string') {
    try { return JSON.stringify(value, null, 2); } catch { return String(value); }
  }
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

/** Short trailing summary (e.g. " · 3 matches"). */
function summarizeResult(call: ToolCallItem, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (call.status !== 'done' || call.result == null) return '';
  try {
    const parsed = typeof call.result === 'string' ? JSON.parse(call.result) : call.result;
    if (!parsed || typeof parsed !== 'object') return '';
    const r = parsed as any;
    if (Array.isArray(r.agents)) return ' → ' + t('counts.agents', { count: r.total ?? r.agents.length });
    if (Array.isArray(r.skills)) return ' → ' + t('counts.skills', { count: r.total ?? r.skills.length });
    if (Array.isArray(r.data_bundles)) return ' → ' + t('counts.bundles', { count: r.total ?? r.data_bundles.length });
    if (Array.isArray(r.matches)) return ' → ' + t('counts.matches', { count: r.matches.length });
    if (r.status === 'proposal_emitted') return ` → proposal pending`;
    if (r.status === 'ok' && r.target_reply) return ` → reply ${r.target_reply.length} chars`;
    if (r.status === 'error' || r.error) return ` → error`;
    if (r.text_prompt) return ` → context loaded`;
    return '';
  } catch {
    return '';
  }
}

export const ToolCallBlock: React.FC<ToolCallBlockProps> = ({ call }) => {
  const { t } = useLanguage();
  const [expanded, setExpanded] = useState(false);
  const signature = useMemo(() => formatCall(call.tool, call.args), [call.tool, call.args]);
  const summary = summarizeResult(call, t);
  const inspector = useInspector();

  // For send_test_message_to_agent + read_eval_conversation we offer
  // an "Open in panel" affordance so the user can replay / continue
  // the eval conversation from the inspector. Anchored to the result
  // payload's eval_conversation_id; only shown once the tool completes
  // successfully.
  //
  // Phase 3 scroll anchor: prefer the assistant message id (the THING
  // this tool call produced) so the panel scrolls right to "what the
  // agent replied" rather than the bottom. Falls back to the user
  // message id, then null (= bottom-scroll).
  const evalOpenable = useMemo(() => {
    if (call.tool !== 'send_test_message_to_agent' && call.tool !== 'read_eval_conversation') {
      return null;
    }
    if (call.status !== 'done') return null;
    // Goes through the shared extractor, which survives a preview truncated
    // mid-JSON. Parsing here with a bare JSON.parse is what left every pill
    // for a long reply disabled — see evalToolResult.ts.
    const parsed = extractEvalRef(call.result);
    if (!parsed) return null;
    return {
      eval_conversation_id: parsed.eval_conversation_id,
      target_agent_name: String(parsed.target_agent_name || 'agent'),
      target_chatbot_id:
        (call.args?.chatbot_id as string | undefined) ??
        parsed.target_chatbot_id ??
        null,
      anchor_message_id:
        parsed.target_assistant_message_id ??
        parsed.target_user_message_id ??
        null,
    };
  }, [call.tool, call.args, call.result, call.status]);

  const openSimulation = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation();
    if (!evalOpenable) return;
    inspector.setActiveSimulation({
      kind: 'simulation',
      eval_conversation_id: evalOpenable.eval_conversation_id,
      target_chatbot_id: evalOpenable.target_chatbot_id,
      target_agent_name: evalOpenable.target_agent_name,
      scrollToMessageId: evalOpenable.anchor_message_id,
    });
    inspector.setCollapsed(false);
  };

  // Dedicated pill rendering for send_test_message_to_agent — mirrors
  // the .aoa-proposal-marker visual idiom (status dot + label + entity
  // + status chip + arrow). User feedback 2026-06-12: the monospace
  // function-call view felt foreign next to the rest of the chat;
  // adopting the proposal-marker shape gives the chat one consistent
  // pill vocabulary for "AoA-fired actions you can open in the panel."
  if (call.tool === 'send_test_message_to_agent') {
    const userMsg = (call.args?.user_message as string | undefined) || '';
    const previewLen = userMsg.length > 70 ? userMsg.slice(0, 67) + '…' : userMsg;
    const statusVariant: 'pending' | 'applied' | 'rejected' = call.status === 'error'
      ? 'rejected'
      : call.status === 'done'
        ? 'applied'
        : 'pending';
    const statusLabel = statusVariant === 'pending' ? 'sending' :
      statusVariant === 'rejected' ? 'failed' : 'reply';
    const handlePillClick = (e: React.MouseEvent | React.KeyboardEvent) => {
      if (evalOpenable) openSimulation(e);
    };
    return (
      <button
        type="button"
        className={[
          'aoa-proposal-marker',
          `is-${statusVariant}`,
          evalOpenable ? '' : 'is-not-clickable',
        ].filter(Boolean).join(' ')}
        onClick={handlePillClick}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && evalOpenable) {
            handlePillClick(e);
          }
        }}
        disabled={!evalOpenable}
        aria-label={
          evalOpenable
            ? `Open test message simulation in side panel`
            : t('metaAgent.testMessageToAgent')
        }
        data-testid={`aoa-tool-call-${call.tool}`}
      >
        <span className={`aoa-proposal-marker__dot is-${statusVariant}`} aria-hidden />
        <span className="aoa-proposal-marker__label">{t('metaAgent.testAgent')}</span>
        {previewLen && (
          <>
            <span className="aoa-proposal-marker__sep" aria-hidden>·</span>
            <span dir="auto" className="aoa-proposal-marker__entity">{previewLen}</span>
          </>
        )}
        <span className={`aoa-proposal-marker__status is-${statusVariant}`}>
          {statusLabel}
        </span>
        {evalOpenable && (
          <svg
            className="aoa-proposal-marker__arrow"
            width="12" height="12" viewBox="0 0 24 24" fill="none"
            stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M7 17L17 7" />
            <path d="M9 7h8v8" />
          </svg>
        )}
      </button>
    );
  }

  return (
    <div
      className={`aoa-toolcall ${call.status === 'error' ? 'is-error' : ''}`}
      data-testid={`aoa-tool-call-${call.tool}`}
    >
      <div
        className="aoa-toolcall-line"
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
      >
        <span className={`aoa-toolcall-dot ${call.status}`} aria-hidden>
          {call.status === 'pending' ? <Spin size="small" /> : null}
        </span>
        <code dir="auto" className="aoa-toolcall-sig">{signature}</code>
        {summary && <span className="aoa-toolcall-summary">{summary}</span>}
        {evalOpenable && (
          <Tooltip
            title={t('metaAgent.openSimulationSidePanel')}
            placement="top"
            overlayClassName="aoa-tooltip"
          >
            <button
              type="button"
              className="aoa-toolcall-open"
              onClick={openSimulation}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openSimulation(e); }}
              aria-label={t('metaAgent.openSimulationSidePanel')}
              data-testid={`aoa-tool-call-open-${call.tool}`}
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
                   stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                   aria-hidden="true">
                <path d="M7 17L17 7" />
                <path d="M9 7h8v8" />
              </svg>
            </button>
          </Tooltip>
        )}
        <span className="aoa-toolcall-chev" aria-hidden>{expanded ? '▾' : '▸'}</span>
      </div>
      {expanded && (
        <div className="aoa-toolcall-detail">
          <div className="aoa-toolcall-detail-label">args</div>
          <pre className="aoa-toolcall-detail-pre">{JSON.stringify(call.args, null, 2)}</pre>
          {call.status === 'done' && call.result != null && (
            <>
              <div className="aoa-toolcall-detail-label">result</div>
              <pre className="aoa-toolcall-detail-pre aoa-toolcall-detail-result" dir="auto">
                {prettyResult(call.result)}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
};
